import { FC, forwardRef, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { faSpinner } from '@fortawesome/pro-solid-svg-icons/faSpinner';
import { FaIcon, Modal, useMessageEventSubscription } from '@fe-learner-common';

const QuizIframe = forwardRef<
  HTMLIFrameElement,
  { src: string; title: string }
>(({ src, title }, ref) => {
  return (
    <iframe
      ref={ref}
      src={src}
      title={title}
      className="h-full w-full rounded-b-2xl"
    />
  );
});
QuizIframe.displayName = 'QuizIframe';

type IframeMessageEvent =
  | 'startAlewebSpinner'
  | 'stopAlewebSpinner'
  | 'closeIframeModal';

export const QuizSurveyModal = ({
  container,
  src,
  title,
  Trigger,
  onClose,
}: {
  container: HTMLElement;
  src: string;
  title: string;
  Trigger: FC<Dialog.DialogTriggerProps>;
  onClose?: () => void;
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const time = useRef(Date.now());
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const close = () => {
    setOpen(false);
    onClose?.();
  };

  useMessageEventSubscription<IframeMessageEvent>({
    enabled: true,
    ref: iframeRef,
    listener: (event) => {
      switch (event.data) {
        case 'startAlewebSpinner':
          setIsLoading(true);
          break;
        case 'stopAlewebSpinner':
          setTimeout(
            () => {
              setIsLoading(false);
            },
            Math.max(0, 1000 - (Date.now() - time.current)),
          );

          break;
        case 'closeIframeModal':
          close();
          break;
      }
    },
  });

  return (
    <Modal
      container={container}
      Trigger={Trigger}
      open={open}
      onOpenChange={(newValue) => {
        if (newValue) {
          setIsLoading(true);
          time.current = Date.now();
        }

        setOpen(newValue);
      }}
      TitleContent={<h1 className="text-title-md">{title}</h1>}
      onClose={() => {
        close();
      }}
      className="inset-x-0 z-[1010] flex h-full w-full md:h-[90%] md:w-[90%] md:max-w-[1200px] "
      overlayClassName="z-[1010] opacity-[1] bg-opacity-50 backdrop-blur-sm"
    >
      <div className="relative h-full flex-1 bg-neutral-100">
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center rounded-b-2xl bg-neutral-0">
            <FaIcon
              icon={faSpinner}
              className="animate-spin text-neutral-500 duration-1000"
              width={40}
              height={40}
            />
          </div>
        )}
        <QuizIframe ref={iframeRef} src={src} title={title} />
      </div>
    </Modal>
  );
};
